import React from "react";
import {
  CheckResult,
  defineComponents,
  DetailsOptical,
  DocumentReaderDetailType,
  DocumentReaderService,
  DocumentReaderWebComponent,
  EventActions,
  GraphicFieldType,
  ImageQualityChecks,
  Result,
} from "@regulaforensics/vp-frontend-document-components";
import { isMobileDevice } from "../../utils/utils";
import { REGULA_SCENARIO } from "../constants";

export interface UseRegulaSdkProps {
  verificationId: string;
  locale: string;
  currentUploadAttempt: number;
  setCurrentUploadAttempt: (value: number) => void;
  setStartPolling: (value: boolean) => void;
}

export interface UseRegulaSdkResults {
  readerRef: React.RefObject<DocumentReaderWebComponent>;
  frontImage: string | undefined;
  backImage: string | undefined;
  hasImageError: boolean;
  handleRetry: () => void;
}

export function useRegulaSdk({
  verificationId,
  locale,
  currentUploadAttempt,
  setCurrentUploadAttempt,
  setStartPolling,
}: UseRegulaSdkProps): UseRegulaSdkResults {
  const readerRef = React.useRef<DocumentReaderWebComponent>(null);

  const [frontImage, setFrontImage] = React.useState<string>();
  const [backImage, setBackImage] = React.useState<string>();
  const [hasImageError, setHasImageError] = React.useState<boolean>(false);

  const isMobile = isMobileDevice();

  React.useEffect(() => {
    const sdk = new DocumentReaderService();
    window.RegulaDocumentSDK = sdk;

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set("verificationId", verificationId);

    sdk.recognizerProcessParam = {
      processParam: {
        scenario: REGULA_SCENARIO,
        multipageProcessing: true,
        updateOCRValidityByGlare: true,
        strictImageQuality: true,
        imageQa: {
          expectedPass: [
            ImageQualityChecks.Glares,
            ImageQualityChecks.Focus,
            ImageQualityChecks.Resolution,
            ImageQualityChecks.Perspective,
            ImageQualityChecks.Bounds,
            ImageQualityChecks.Portrait,
            ImageQualityChecks.Brightness,
          ],
          focusCheck: true,
          glaresCheck: true,
        },
      },
      delegateProcessing: {
        serviceURL: isMobile ? undefined : currentUrl.toString(),
        delegateURL: isMobile ? undefined : currentUrl.toString(),
      },
    };

    const isDev = import.meta.env.DEV;
    const license = import.meta.env.VITE_REGULA_DEV_LICENSE_KEY;
    defineComponents().then(() => {
      const sdkOptions = isDev && license ? { license } : undefined;
      sdk.initialize(sdkOptions);
    });

    return () => {
      sdk.shutdown();
      sdk.initializeListener = null;
    };
  }, [isMobile, verificationId]);

  React.useEffect(() => {
    const handleRegulaDocReaderSettings = () => {
      if (readerRef.current) {
        readerRef.current.settings = {
          internalScenario: REGULA_SCENARIO,
          locale: locale || "en",
          startScreen: true,
          uploadFileButton: false,
          captureButton: false,
          copyright: false,
          closeButton: true,
          skipButton: false,
        };
      }
    };

    const handleImageUploadComplete = (detail: DocumentReaderDetailType) => {
      const response = detail?.data?.response;
      const fieldList = response?.images?.fieldList;
      const status = response?.status;

      const valueList =
        fieldList?.find((field) => field.fieldType === GraphicFieldType.DOCUMENT_FRONT)
          ?.valueList ?? [];

      const findImageByPage = (pageIndex: number) =>
        valueList.find(
          (val) => val.pageIndex === pageIndex && val.containerType === Result.RAW_UNCROPPED_IMAGE,
        );

      const findImageErrors = (): boolean => {
        const detailsOptical: DetailsOptical | undefined = status?.detailsOptical;

        const imageQAError =
          detailsOptical?.imageQA === CheckResult.ERROR ||
          detailsOptical?.imageQA === CheckResult.WAS_NOT_DONE;
        const expiryError = detailsOptical?.expiry === CheckResult.ERROR;
        const textError = detailsOptical?.text === CheckResult.ERROR;
        const mrzError = detailsOptical?.mrz === CheckResult.ERROR;

        return imageQAError || expiryError || textError || mrzError;
      };

      if (fieldList?.length) {
        const front = findImageByPage(0);
        const back = findImageByPage(1);

        setFrontImage(front?.value);
        setBackImage(back?.value);

        setHasImageError(findImageErrors());
      }
    };

    const handleMobileHandoff = () => {
      if (!isMobile) {
        setStartPolling(true);
      }
    };

    const onDocumentReaderEvent = (event: Event) => {
      const customEvent = event as CustomEvent<DocumentReaderDetailType>;
      const detail = customEvent?.detail;
      const eventAction: EventActions = detail?.action;

      switch (eventAction) {
        case EventActions.ELEMENT_VISIBLE:
          handleRegulaDocReaderSettings();
          break;

        case EventActions.PROCESS_FINISHED:
          handleImageUploadComplete(detail);
          break;

        case EventActions.PRESS_REMOTE_DEVICE_BUTTON:
          handleMobileHandoff();
          break;

        default:
          break;
      }
    };

    window.addEventListener("document-reader", onDocumentReaderEvent);

    return () => {
      window.removeEventListener("document-reader", onDocumentReaderEvent);
    };
  }, [isMobile, locale]);

  const handleRetry = () => {
    setFrontImage(undefined);
    setBackImage(undefined);
    setHasImageError(false);
    setCurrentUploadAttempt(currentUploadAttempt + 1);
  };

  return {
    readerRef,
    frontImage,
    backImage,
    hasImageError,
    handleRetry,
  };
}
